<template>
  <div class="admin-api-usage px-2">
    <!-- 顶栏标题与控制栏 -->
    <div class="d-flex flex-wrap justify-content-between align-items-center mb-3">
      <div class="d-flex align-items-center mb-2 mb-md-0">
        <h4 class="mb-0 text-white font-weight-bold" style="font-size: 1.25rem;">
          📊 API 用量报表
        </h4>
        <b-badge :variant="summaryData.is_today ? 'success' : 'info'" class="ml-2 px-2 py-1">
          {{ summaryData.is_today ? '🔥 Redis 实时聚合' : '💾 MySQL 历史归档' }}
        </b-badge>
      </div>

      <div class="d-flex flex-wrap align-items-center">
        <!-- 快捷日期按钮 -->
        <b-button-group size="sm" class="mr-2 mb-2 mb-sm-0">
          <b-button
            :variant="selectedDate === todayStr ? 'primary' : 'outline-secondary'"
            @click="setQuickDate('today')"
          >
            今日
          </b-button>
          <b-button
            :variant="selectedDate === yesterdayStr ? 'primary' : 'outline-secondary'"
            @click="setQuickDate('yesterday')"
          >
            昨日
          </b-button>
        </b-button-group>

        <!-- 日期选择器 -->
        <div class="mr-2 mb-2 mb-sm-0" style="width: 170px;">
          <b-form-datepicker
            v-model="selectedDate"
            size="sm"
            locale="zh"
            placeholder="选择日期"
            :date-format-options="{ year: 'numeric', month: '2-digit', day: '2-digit' }"
            @input="fetchSummary"
            class="bg-dark text-white border-secondary"
          ></b-form-datepicker>
        </div>

        <!-- 刷新与归档操作 -->
        <b-button
          variant="outline-primary"
          size="sm"
          class="mr-2 mb-2 mb-sm-0"
          @click="fetchSummary"
          :disabled="loading"
        >
          <b-icon icon="arrow-clockwise" :animation="loading ? 'spin' : ''"></b-icon>
          刷新
        </b-button>

        <b-button
          variant="outline-warning"
          size="sm"
          class="mb-2 mb-sm-0"
          @click="triggerArchive"
          :disabled="archiving"
          title="将当前所选日期的数据从 Redis 同步落盘至 MySQL"
        >
          <b-icon icon="cloud-upload" :animation="archiving ? 'spin' : ''"></b-icon>
          {{ archiving ? '归档中...' : '同步落盘' }}
        </b-button>
      </div>
    </div>

    <!-- 4 大核心指标卡片 -->
    <div class="row mb-3">
      <div class="col-xl-3 col-sm-6 col-12 mb-2 mb-xl-0">
        <div class="card bg-dark-soft border-0 shadow-sm p-3 stat-card">
          <div class="text-white-50 small mb-1">活跃调用用户</div>
          <div class="d-flex justify-content-between align-items-baseline">
            <span class="h3 font-weight-bold text-white mb-0">{{ summaryData.total_users || 0 }}</span>
            <span class="text-muted small">人</span>
          </div>
        </div>
      </div>

      <div class="col-xl-3 col-sm-6 col-12 mb-2 mb-xl-0">
        <div class="card bg-dark-soft border-0 shadow-sm p-3 stat-card">
          <div class="text-white-50 small mb-1">API 请求总量</div>
          <div class="d-flex justify-content-between align-items-baseline">
            <span class="h3 font-weight-bold text-success mb-0">{{ formatNumber(summaryData.total_requests || 0) }}</span>
            <span class="text-muted small">次</span>
          </div>
        </div>
      </div>

      <div class="col-xl-3 col-sm-6 col-12 mb-2 mb-xl-0">
        <div class="card bg-dark-soft border-0 shadow-sm p-3 stat-card">
          <div class="text-white-50 small mb-1">涉及业务接口</div>
          <div class="d-flex justify-content-between align-items-baseline">
            <span class="h3 font-weight-bold text-info mb-0">{{ Object.keys(summaryData.endpoints_summary || {}).length }}</span>
            <span class="text-muted small">个</span>
          </div>
        </div>
      </div>

      <div class="col-xl-3 col-sm-6 col-12 mb-2 mb-xl-0">
        <div class="card bg-dark-soft border-0 shadow-sm p-3 stat-card">
          <div class="text-white-50 small mb-1">平均每人请求</div>
          <div class="d-flex justify-content-between align-items-baseline">
            <span class="h3 font-weight-bold text-warning mb-0">{{ avgRequestsPerUser }}</span>
            <span class="text-muted small">次 / 人</span>
          </div>
        </div>
      </div>
    </div>

    <!-- 图表分析栏 -->
    <div class="row mb-3" v-if="!loading && summaryData.total_requests > 0">
      <!-- 接口调用占比图 -->
      <div class="col-lg-6 col-12 mb-3 mb-lg-0">
        <div class="card bg-dark-soft border-0 shadow-sm p-3 h-100">
          <h6 class="text-white font-weight-bold mb-2 border-left border-primary pl-2">
            各业务接口调用分布
          </h6>
          <div style="height: 280px;">
            <v-chart :options="endpointPieOption" :autoresize="true" style="width: 100%; height: 100%;"></v-chart>
          </div>
        </div>
      </div>

      <!-- 全天 24 小时聚合分布图 -->
      <div class="col-lg-6 col-12">
        <div class="card bg-dark-soft border-0 shadow-sm p-3 h-100">
          <h6 class="text-white font-weight-bold mb-2 border-left border-info pl-2">
            全天 24 小时请求峰值分布
          </h6>
          <div style="height: 280px;">
            <v-chart :options="hourlyBarOption" :autoresize="true" style="width: 100%; height: 100%;"></v-chart>
          </div>
        </div>
      </div>
    </div>

    <!-- 用户调用排行 Top 100 榜单 -->
    <div class="card bg-dark-soft border-0 shadow-sm">
      <div class="card-header bg-transparent border-secondary d-flex justify-content-between align-items-center py-2 px-3">
        <span class="font-weight-bold text-white">
          🏆 用户调用量排行 (Top 100)
        </span>
        <span class="text-muted small" v-if="summaryData.top_users">
          共 {{ summaryData.top_users.length }} 位活跃用户
        </span>
      </div>

      <div class="card-body p-0">
        <div v-if="loading" class="text-center py-5">
          <b-spinner variant="primary"></b-spinner>
          <div class="text-muted small mt-2">正在聚合用量数据...</div>
        </div>

        <div v-else-if="!summaryData.top_users || summaryData.top_users.length === 0" class="text-center py-5 text-muted">
          所选日期 ({{ selectedDate }}) 暂无用户 API 调用记录
        </div>

        <template v-else>
          <!-- PC 端表格视图 -->
          <div class="d-none d-lg-block">
            <div class="table-responsive">
              <table class="table table-dark table-hover table-sm mb-0">
                <thead>
                  <tr>
                    <th style="width: 70px;" class="text-center">排名</th>
                    <th style="width: 90px;">用户 ID</th>
                    <th style="width: 150px;">用户名 / 昵称</th>
                    <th style="width: 130px;">用户 IP</th>
                    <th style="width: 130px;">调用总次数</th>
                    <th style="width: 140px;">调用占比</th>
                    <th style="width: 220px;">主要接口</th>
                    <th style="width: 100px;" class="text-center">操作</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(u, idx) in summaryData.top_users" :key="u.user_id">
                    <td class="text-center font-weight-bold align-middle">
                      <span v-if="idx === 0" class="badge badge-warning text-dark px-2 py-1">🥇 1</span>
                      <span v-else-if="idx === 1" class="badge badge-secondary px-2 py-1">🥈 2</span>
                      <span v-else-if="idx === 2" class="badge badge-danger px-2 py-1">🥉 3</span>
                      <span v-else class="text-muted">#{{ idx + 1 }}</span>
                    </td>
                    <td class="align-middle">
                      <code class="text-info font-weight-bold">{{ u.user_id }}</code>
                    </td>
                    <td class="align-middle">
                      <div class="text-white font-weight-bold text-truncate" style="max-width: 140px;">
                        {{ u.user_nick || u.user_name || '-' }}
                      </div>
                      <div class="text-muted x-small text-truncate" style="max-width: 140px;" v-if="u.user_nick && u.user_name">
                        @{{ u.user_name }}
                      </div>
                    </td>
                    <td class="align-middle">
                      <code class="text-warning small font-weight-bold" v-if="u.last_ip">{{ u.last_ip }}</code>
                      <span class="text-muted small" v-else>-</span>
                    </td>
                    <td class="align-middle font-weight-bold text-success">
                      {{ formatNumber(u.total_count) }} 次
                    </td>
                    <td class="align-middle" style="width: 140px;">
                      <div class="d-flex align-items-center">
                        <div class="progress flex-grow-1 mr-2 bg-secondary" style="height: 6px;">
                          <div
                            class="progress-bar bg-success"
                            :style="{ width: calcPercent(u.total_count, summaryData.total_requests) + '%' }"
                          ></div>
                        </div>
                        <span class="x-small text-white-50" style="min-width: 45px;">
                          {{ calcPercent(u.total_count, summaryData.total_requests) }}%
                        </span>
                      </div>
                    </td>
                    <td class="align-middle">
                      <div class="d-flex flex-wrap">
                        <span
                          v-for="(count, ep) in getTopEndpoints(u.endpoints, 2)"
                          :key="ep"
                          class="badge badge-secondary mr-1 mb-1"
                          style="font-size: 0.75rem;"
                        >
                          {{ formatEndpoint(ep) }}: {{ count }}
                        </span>
                      </div>
                    </td>
                    <td class="text-center align-middle">
                      <b-button
                        variant="outline-info"
                        size="sm"
                        class="px-2 py-0"
                        style="font-size: 0.8rem;"
                        @click="openUserDetail(u)"
                      >
                        详情分布
                      </b-button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- 移动端卡片视图 -->
          <div class="d-lg-none p-2">
            <div
              v-for="(u, idx) in summaryData.top_users"
              :key="u.user_id"
              class="card bg-dark border-secondary p-3 mb-2 shadow-sm"
            >
              <div class="d-flex justify-content-between align-items-center mb-1">
                <div class="d-flex align-items-center text-truncate mr-2">
                  <span class="badge badge-secondary mr-2">#{{ idx + 1 }}</span>
                  <span class="font-weight-bold text-white text-truncate">{{ u.user_nick || u.user_name }}</span>
                  <span class="text-muted small ml-1">(UID: {{ u.user_id }})</span>
                </div>
                <b-badge variant="success">{{ formatNumber(u.total_count) }} 次</b-badge>
              </div>

              <div class="small d-flex justify-content-between text-muted mb-2">
                <span>调用占比: {{ calcPercent(u.total_count, summaryData.total_requests) }}%</span>
                <span v-if="u.last_ip">IP: <code class="text-warning">{{ u.last_ip }}</code></span>
              </div>

              <div class="d-flex justify-content-between align-items-center mt-2 pt-2 border-top border-secondary">
                <div class="d-flex flex-wrap">
                  <span
                    v-for="(count, ep) in getTopEndpoints(u.endpoints, 2)"
                    :key="ep"
                    class="badge badge-secondary mr-1"
                  >
                    {{ formatEndpoint(ep) }}: {{ count }}
                  </span>
                </div>
                <b-button variant="outline-info" size="sm" @click="openUserDetail(u)">
                  详情
                </b-button>
              </div>
            </div>
          </div>
        </template>
      </div>
    </div>

    <!-- 用户用量明细弹窗 Modal -->
    <b-modal
      v-model="showUserDetailModal"
      :title="`用户 API 用量明细 - ${selectedUser ? (selectedUser.user_nick || selectedUser.user_name) : ''} (UID: ${selectedUser ? selectedUser.user_id : ''})`"
      size="lg"
      hide-footer
      header-bg-variant="dark"
      header-text-variant="white"
      body-bg-variant="dark"
      body-text-variant="white"
    >
      <div v-if="selectedUser">
        <div class="d-flex flex-wrap justify-content-between align-items-center p-3 mb-3 bg-dark-soft rounded">
          <div class="mr-3 mb-2 mb-md-0">
            <div class="text-muted small">统计日期</div>
            <div class="h6 text-white font-weight-bold mb-0">{{ selectedDate }}</div>
          </div>
          <div class="mr-3 mb-2 mb-md-0">
            <div class="text-muted small">用户最后 IP</div>
            <div class="h6 text-warning font-weight-bold mb-0">
              <code class="text-warning" v-if="selectedUser.last_ip">{{ selectedUser.last_ip }}</code>
              <span v-else class="text-muted">-</span>
            </div>
          </div>
          <div class="mr-3 mb-2 mb-md-0">
            <div class="text-muted small">全天调用总量</div>
            <div class="h5 text-success font-weight-bold mb-0">{{ formatNumber(selectedUser.total_count) }} 次</div>
          </div>
          <div>
            <div class="text-muted small">涉及接口数</div>
            <div class="h6 text-info font-weight-bold mb-0">{{ Object.keys(selectedUser.endpoints || {}).length }} 个</div>
          </div>
        </div>

        <!-- 24小时分布柱状图 -->
        <div class="card bg-dark-soft border-0 p-3 mb-3">
          <h6 class="text-white font-weight-bold mb-2 border-left border-primary pl-2">
            24 小时每小时请求次数
          </h6>
          <div style="height: 220px;">
            <v-chart :options="userDetailHourlyOption" :autoresize="true" style="width: 100%; height: 100%;"></v-chart>
          </div>
        </div>

        <!-- 接口调用明细表格 -->
        <div class="card bg-dark-soft border-0 p-3">
          <h6 class="text-white font-weight-bold mb-2 border-left border-info pl-2">
            各业务接口调用明细
          </h6>
          <div class="table-responsive">
            <table class="table table-dark table-sm table-hover mb-0">
              <thead>
                <tr>
                  <th>接口名 (endpoint)</th>
                  <th>业务说明</th>
                  <th class="text-right">请求次数</th>
                  <th class="text-right" style="width: 100px;">占比</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(cnt, ep) in selectedUser.endpoints" :key="ep">
                  <td><code class="text-info font-weight-bold">{{ ep }}</code></td>
                  <td class="text-white-50">{{ formatEndpoint(ep) }}</td>
                  <td class="text-right text-success font-weight-bold">{{ formatNumber(cnt) }}</td>
                  <td class="text-right text-muted small">
                    {{ calcPercent(cnt, selectedUser.total_count) }}%
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import moment from 'moment'
import { request_zzquant } from '@/network/request.js'

export default {
  name: 'AdminApiUsage',
  data() {
    const today = moment().format('YYYY-MM-DD')
    const yesterday = moment().subtract(1, 'days').format('YYYY-MM-DD')
    return {
      todayStr: today,
      yesterdayStr: yesterday,
      selectedDate: today,
      loading: false,
      archiving: false,
      summaryData: {
        stat_date: today,
        is_today: true,
        prev_date: yesterday,
        total_users: 0,
        total_requests: 0,
        endpoints_summary: {},
        hourly_summary: {},
        prev_hourly_summary: {},
        top_users: [],
      },
      showUserDetailModal: false,
      selectedUser: null,
      friendlyEndpoints: {
        stk_mins: '个股分钟K线 (stk_mins)',
        daily: '日线行情 (daily)',
        open_stock_info: '股票板块信息 (open_stock_info)',
        lhb: '龙虎榜数据 (lhb)',
        sentiment: '市场情绪指标 (sentiment)',
        plate_kline: '板块K线数据 (plate_kline)',
        uplimit: '涨停梯队池 (uplimit)',
        realtime: '实时快照 (realtime)',
      }
    }
  },
  computed: {
    avgRequestsPerUser() {
      if (!this.summaryData.total_users || !this.summaryData.total_requests) return 0
      return Math.round(this.summaryData.total_requests / this.summaryData.total_users)
    },
    endpointPieOption() {
      const summary = this.summaryData.endpoints_summary || {}
      const data = Object.keys(summary).map(ep => ({
        name: this.formatEndpoint(ep),
        value: summary[ep]
      }))
      return {
        tooltip: {
          trigger: 'item',
          formatter: '{b}: {c} 次 ({d}%)'
        },
        legend: {
          orient: 'vertical',
          right: '5%',
          top: 'center',
          textStyle: { color: '#94a3b8' }
        },
        series: [
          {
            name: '接口请求量',
            type: 'pie',
            radius: ['45%', '70%'],
            center: ['40%', '50%'],
            avoidLabelOverlap: false,
            label: { show: false },
            emphasis: {
              label: {
                show: true,
                fontSize: '14',
                fontWeight: 'bold',
                color: '#fff'
              }
            },
            data: data
          }
        ]
      }
    },
    hourlyBarOption() {
      const isToday = !!this.summaryData.is_today
      const curDateLabel = isToday ? '今日' : (this.summaryData.stat_date || this.selectedDate)
      const prevDateLabel = isToday
        ? '昨日同时间'
        : (this.summaryData.prev_date ? `前一日 (${this.summaryData.prev_date})` : '前一日')

      const xData = []
      const curYData = []
      const prevYData = []

      const curHourly = this.summaryData.hourly_summary || {}
      const prevHourly = this.summaryData.prev_hourly_summary || {}

      // 兜底：若后端未返回全量 hourly_summary，则聚合 top_users
      const fallbackHourly = {}
      if (Object.keys(curHourly).length === 0) {
        const users = this.summaryData.top_users || []
        users.forEach(u => {
          const h = u.hourly || {}
          Object.keys(h).forEach(k => {
            const hNorm = (k.length === 1 ? '0' : '') + k
            fallbackHourly[hNorm] = (fallbackHourly[hNorm] || 0) + (h[k] || 0)
          })
        })
      }

      for (let i = 0; i < 24; i++) {
        const hKey = (i < 10 ? '0' : '') + i
        xData.push(`${hKey}:00`)
        const curVal = curHourly[hKey] !== undefined ? curHourly[hKey] : (fallbackHourly[hKey] || 0)
        const prevVal = prevHourly[hKey] !== undefined ? prevHourly[hKey] : 0
        curYData.push(curVal)
        prevYData.push(prevVal)
      }

      return {
        tooltip: {
          trigger: 'axis',
          axisPointer: { type: 'shadow' },
          formatter: (params) => {
            if (!params || !params.length) return ''
            const timeLabel = params[0].name
            let html = `<div style="font-weight:bold;margin-bottom:6px;font-size:13px;">⏰ ${timeLabel} 请求分布</div>`
            let curVal = 0
            let prevVal = 0
            params.forEach(p => {
              const marker = p.marker
              const seriesName = p.seriesName
              const val = p.value || 0
              if (p.seriesIndex === 0) curVal = val
              else if (p.seriesIndex === 1) prevVal = val
              html += `<div style="display:flex;justify-content:space-between;align-items:center;gap:16px;margin-bottom:3px;">
                <span>${marker} ${seriesName}</span>
                <span style="font-weight:bold;color:#f8fafc;">${Number(val).toLocaleString()} 次</span>
              </div>`
            })
            if (params.length >= 2) {
              const diff = curVal - prevVal
              const diffSign = diff > 0 ? '+' : ''
              const pct = prevVal > 0 ? ((diff / prevVal) * 100).toFixed(1) + '%' : (curVal > 0 ? '+100%' : '0%')
              const diffColor = diff > 0 ? '#4ade80' : (diff < 0 ? '#f87171' : '#94a3b8')
              html += `<div style="margin-top:6px;padding-top:5px;border-top:1px dashed #475569;display:flex;justify-content:space-between;align-items:center;gap:16px;color:${diffColor};font-size:12px;">
                <span>较前一日对比</span>
                <span style="font-weight:bold;">${diffSign}${Number(diff).toLocaleString()} 次 (${diffSign}${pct})</span>
              </div>`
            }
            return html
          }
        },
        legend: {
          data: [curDateLabel, prevDateLabel],
          top: '2%',
          right: '3%',
          textStyle: { color: '#cbd5e1', fontSize: 12 }
        },
        grid: {
          top: '16%',
          left: '3%',
          right: '4%',
          bottom: '8%',
          containLabel: true
        },
        xAxis: {
          type: 'category',
          data: xData,
          axisLine: { lineStyle: { color: '#475569' } },
          axisLabel: {
            color: '#94a3b8',
            interval: (index) => index % 2 === 0,
            showMinLabel: true
          }
        },
        yAxis: {
          type: 'value',
          axisLine: { lineStyle: { color: '#475569' } },
          splitLine: { lineStyle: { color: '#334155' } },
          axisLabel: { color: '#94a3b8' }
        },
        series: [
          {
            name: curDateLabel,
            type: 'bar',
            data: curYData,
            barMaxWidth: 14,
            itemStyle: {
              color: '#38bdf8',
              borderRadius: [3, 3, 0, 0]
            }
          },
          {
            name: prevDateLabel,
            type: 'bar',
            data: prevYData,
            barMaxWidth: 14,
            itemStyle: {
              color: '#a855f7',
              opacity: 0.85,
              borderRadius: [3, 3, 0, 0]
            }
          }
        ]
      }
    },
    userDetailHourlyOption() {
      if (!this.selectedUser) return {}
      const h = this.selectedUser.hourly || {}
      const xData = []
      const yData = []
      for (let i = 0; i < 24; i++) {
        const hStr = (i < 10 ? '0' : '') + i
        xData.push(`${hStr}:00`)
        yData.push(h[hStr] || h[String(i)] || 0)
      }
      return {
        tooltip: {
          trigger: 'axis',
          axisPointer: { type: 'shadow' }
        },
        grid: {
          top: '12%',
          left: '3%',
          right: '4%',
          bottom: '10%',
          containLabel: true
        },
        xAxis: {
          type: 'category',
          data: xData,
          axisLine: { lineStyle: { color: '#475569' } },
          axisLabel: {
            color: '#94a3b8',
            interval: (index) => index % 2 === 0,
            showMinLabel: true
          }
        },
        yAxis: {
          type: 'value',
          axisLine: { lineStyle: { color: '#475569' } },
          splitLine: { lineStyle: { color: '#334155' } },
          axisLabel: { color: '#94a3b8' }
        },
        series: [
          {
            name: '小时请求数',
            type: 'bar',
            data: yData,
            barMaxWidth: 16,
            itemStyle: {
              color: '#22c55e',
              borderRadius: [3, 3, 0, 0]
            }
          }
        ]
      }
    }
  },
  mounted() {
    this.fetchSummary()
  },
  methods: {
    setQuickDate(type) {
      if (type === 'today') {
        this.selectedDate = this.todayStr
      } else if (type === 'yesterday') {
        this.selectedDate = this.yesterdayStr
      }
      this.fetchSummary()
    },
    async fetchSummary() {
      this.loading = true
      try {
        const res = await request_zzquant({
          url: '/v3/admin/api-usage/summary',
          method: 'GET',
          params: { stat_date: this.selectedDate }
        })
        if (res.data && (res.data.code === 20000 || res.data.code === 200)) {
          this.summaryData = res.data.data
        } else {
          this.$bvToast.toast((res.data && (res.data.message || res.data.msg)) || '获取数据失败', {
            title: '错误',
            variant: 'danger',
            solid: true
          })
        }
      } catch (err) {
        console.error('fetchSummary error:', err)
        this.$bvToast.toast('网络请求异常', {
          title: '错误',
          variant: 'danger',
          solid: true
        })
      } finally {
        this.loading = false
      }
    },
    async triggerArchive() {
      this.archiving = true
      try {
        const res = await request_zzquant({
          url: '/v3/admin/api-usage/trigger-archive',
          method: 'POST',
          params: { stat_date: this.selectedDate }
        })
        if (res.data && (res.data.code === 20000 || res.data.code === 200)) {
          const d = res.data.data
          this.$bvToast.toast(
            `归档成功！已持久化 ${d.total_users} 位用户，共 ${d.total_requests} 次请求 (耗时 ${d.duration_ms}ms)`,
            {
              title: '归档成功',
              variant: 'success',
              solid: true
            }
          )
          this.fetchSummary()
        } else {
          this.$bvToast.toast((res.data && (res.data.message || res.data.msg)) || '归档失败', {
            title: '失败',
            variant: 'danger',
            solid: true
          })
        }
      } catch (err) {
        this.$bvToast.toast('触发归档请求异常', {
          title: '错误',
          variant: 'danger',
          solid: true
        })
      } finally {
        this.archiving = false
      }
    },
    openUserDetail(user) {
      this.selectedUser = user
      this.showUserDetailModal = true
    },
    formatNumber(num) {
      if (!num) return '0'
      return num.toLocaleString()
    },
    calcPercent(part, total) {
      if (!total || !part) return '0.0'
      return ((part / total) * 100).toFixed(1)
    },
    formatEndpoint(ep) {
      return this.friendlyEndpoints[ep] || ep
    },
    getTopEndpoints(endpointsMap, n) {
      if (!endpointsMap) return {}
      const res = {}
      const entries = Object.entries(endpointsMap).slice(0, n)
      entries.forEach(([k, v]) => {
        res[k] = v
      })
      return res
    }
  }
}
</script>

<style scoped>
.admin-api-usage {
  color: #fff;
}

.bg-dark-soft {
  background-color: #1e293b !important;
  border-radius: 8px;
}

.stat-card {
  transition: transform 0.2s, box-shadow 0.2s;
}

.stat-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.3) !important;
}

.x-small {
  font-size: 0.75rem;
}
</style>
